import { useEffect, useState } from "react";
import { Bell, X } from "@phosphor-icons/react";
import { toast } from "sonner";
import { usePushSubscription } from "@/hooks/usePushSubscription";

const DISMISS_KEY = "hub.push.onboarding_dismissed_at";
const DISMISS_TTL_MS = 7 * 24 * 60 * 60 * 1000; // 7 days per spec

/**
 * Onboarding pill for Web Push. Appears in the inbox 6 s after mount if:
 *   - the browser supports push,
 *   - the user has not yet subscribed,
 *   - permission is not "denied" (nothing we can do about that),
 *   - and the user has not dismissed the banner in the last 7 days.
 *
 * Uses `visibilityState` so it doesn't animate in while the tab is
 * backgrounded — otherwise the agent comes back and finds an already-shown
 * banner they didn't watch appear.
 */
export default function PushOnboardingBanner() {
    const { supported, permission, subscribed, loading, subscribe } = usePushSubscription();
    const [dismissed, setDismissed] = useState(true); // start dismissed; enable after grace period
    const [busy, setBusy] = useState(false);

    // Check localStorage once + start the 6-second grace timer.
    useEffect(() => {
        try {
            const raw = localStorage.getItem(DISMISS_KEY);
            if (raw) {
                const ts = parseInt(raw, 10);
                if (!Number.isNaN(ts) && Date.now() - ts < DISMISS_TTL_MS) {
                    setDismissed(true);
                    return;
                }
            }
        } catch { /* ignore */ }
        const t = setTimeout(() => setDismissed(false), 6000);
        return () => clearTimeout(t);
    }, []);

    if (!supported || loading || subscribed || permission === "denied" || dismissed) return null;

    const onEnable = async () => {
        setBusy(true);
        const res = await subscribe();
        setBusy(false);
        if (res.ok) {
            toast.success("Notificaciones activadas para este dispositivo");
            // Persist dismissal so the banner doesn't come back on other pages.
            try { localStorage.setItem(DISMISS_KEY, String(Date.now())); }
            catch { /* ignore */ }
        } else {
            toast.error(res.error || "No se pudo activar el push");
        }
    };

    const onDismiss = () => {
        try { localStorage.setItem(DISMISS_KEY, String(Date.now())); }
        catch { /* ignore */ }
        setDismissed(true);
    };

    return (
        <div
            // vh-safe-top: respect env(safe-area-inset-top) on iOS PWA
            // standalone so the banner doesn't sit UNDER the status bar
            // (clock + wifi/battery). Renders as 0 on desktop, ~47px on
            // iPhones with notch. Same pattern as InboxMobile's TopBar.
            className="flex items-center gap-2 sm:gap-3 px-4 py-2 sm:py-2 pt-2 border-b border-brand-200 bg-brand-50 text-brand-900 shrink-0 vh-safe-top"
            role="status"
            data-testid="push-onboarding-banner"
        >
            <Bell size={16} weight="fill" className="text-brand-700 shrink-0" />
            <span className="text-xs sm:text-sm flex-1 min-w-0">
                ¿Querés recibir notificaciones cuando entren mensajes, aunque tengas el Hub cerrado?
            </span>
            <button
                type="button"
                onClick={onEnable}
                disabled={busy}
                className="vh-tappable shrink-0 text-xs font-semibold px-3 py-1.5 rounded-full bg-brand-600 text-white hover:bg-brand-700 disabled:opacity-60 transition-colors"
                data-testid="push-onboarding-enable"
            >
                {busy ? "Activando…" : "Activar"}
            </button>
            <button
                type="button"
                onClick={onDismiss}
                className="vh-tappable shrink-0 text-xs font-medium px-2.5 py-1.5 rounded-md text-brand-700 hover:bg-brand-100 transition-colors"
                data-testid="push-onboarding-later"
            >
                Ahora no
            </button>
            <button
                type="button"
                onClick={onDismiss}
                className="vh-tappable shrink-0 w-6 h-6 rounded-md text-brand-700 hover:bg-brand-100 flex items-center justify-center transition-colors"
                aria-label="Descartar aviso push"
                title="Descartar"
                data-testid="push-onboarding-dismiss"
            >
                <X size={12} weight="bold" />
            </button>
        </div>
    );
}
